import React, { useEffect, useRef, useState } from "react";

import {
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { useNavigate } from "react-router-dom";
import { useClientInfo, useAppContext, axiosInstance } from "../utils/commonImports";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { drawClientHeader } from "../utils/reportClientHeader";
import MyDatePicker from "../components/MyDatePicker";
import { addPdfFooter } from "../utils/reportFooter";
import VehicleDropdown from "../components/VehicleDropdown";
import VendorDropdown from "../components/VendorDropdown";
import { formatDate, resolveDateRange, printPdfDoc } from "../utils/fy";

const REPORTS_PATH = "/reports"; // change if your dashboard route differs

/* ===============================
   COLUMNS (SINGLE SOURCE)
================================ */
const FUEL_INDENT_COLUMNS = [
  { key: "indent_number", label: "Indent No", width: 100 },
  { key: "indent_date", label: "Date", width: 80 },
  { key: "bunk_name", label: "Fuel Bunk", width: 130 },
  { key: "vehicle_display", label: "Vehicle / Vendor", width: 100 },
  { key: "driver_name", label: "Driver", width: 100 },
  { key: "fuel_type", label: "Fuel", width: 70 },
  { key: "fuel_qty", label: "Qty", width: 70, align: "right" },
  { key: "fuel_rate", label: "Rate", width: 70, align: "right" },
  { key: "fuel_amount", label: "Fuel Amt", width: 90, align: "right" },
  { key: "oil_amount", label: "Oil", width: 70, align: "right" },
  { key: "other_amount", label: "Other", width: 70, align: "right" },
  { key: "fuel_surcharge", label: "Surcharge", width: 80, align: "right" },
  { key: "total_amount", label: "Total", width: 90, align: "right" },
  { key: "run_kms", label: "Run KM", width: 70, align: "right" },
  { key: "status", label: "Status", width: 70, align: "center" },
];

const formatCell = (key, r) => {
  if (key === "indent_date") return formatDate(r[key]);
  if (key === "run_kms") return Math.round(Number(r[key] || 0));
  return r[key] ?? "";
};

export default function FuelIndentReport() {
  const navigate = useNavigate();

  const { clientId, client } = useClientInfo();
  const { selectedFinancialYear, financialYearLoading } = useAppContext();
  const fy = selectedFinancialYear?.label || ""; // display label e.g. "2025-26"

  const [rows, setRows] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status] = useState("");
  const [search, setSearch] = useState("");

  const [vehicle, setVehicle] = useState(null);
  const [vendor, setVendor] = useState(null);

  const [filterBunk, setFilterBunk] = useState("");
  const [bunks, setBunks] = useState([]);
  const [bunk, setBunk] = useState(null);

  const didMount = useRef(false);

  /* ===============================
     BACK BUTTON -> always Reports dashboard
  ================================ */
  const handleBack = () => navigate(REPORTS_PATH);

  /* ===============================
     LOAD REPORT (FY aware)
  ================================ */
  const loadReport = async ({ ignoreDates = false } = {}) => {
    try {
      const { from, to } = resolveDateRange(selectedFinancialYear, fromDate, toDate, ignoreDates);

      const res = await axiosInstance.get("/api/indents/report", {
        params: {
          client_id: clientId,
          from_date: from,
          to_date: to,
          fy_id: selectedFinancialYear?.fyid || "",
          fy, // lets the backend filter too, if it supports it
          status,
          search,
          vehicle_id: vehicle?.vehicle_id || "",
          vendor_id: vendor?.vendor_id || "",
          bunk_id: filterBunk || "",
        },
      });

      const data = (res.data || []).map((r) => {
        let vehicleDisplay = "";

        if (r.is_vendor_indent) {
          const vendorName = r.vendor_name || "";
          const vendorVehicleNo = r.vendor_vehicle_number || "";

          if (vendorName && vendorVehicleNo) {
            vehicleDisplay = `${vendorName} (${vendorVehicleNo})`;
          } else if (vendorName) {
            vehicleDisplay = vendorName;
          } else if (vendorVehicleNo) {
            vehicleDisplay = vendorVehicleNo;
          }
        } else {
          vehicleDisplay = r.vehicle_number || "";
        }

        return { ...r, vehicle_display: vehicleDisplay };
      });

      setRows(data);
    } catch (err) {
      console.error("Failed to load fuel indent report:", err);
      setRows([]);
    }
  };

  // Client or FY changed: clear manual dates and load the whole FY
  useEffect(() => {
    if (!clientId || financialYearLoading) return;
    setFromDate("");
    setToDate("");
    loadReport({ ignoreDates: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, selectedFinancialYear?.fyid, financialYearLoading]);

  // Dropdown filters changed: reload (skip first run, the effect above loads it)
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (clientId) loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle, vendor, filterBunk]);

  // Load bunks list
  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/bunks", { params: { client_id: clientId } })
      .then((res) => setBunks(res.data || []))
      .catch((err) => console.error("Failed to load bunks:", err));
  }, [clientId]);

  /* ===============================
     SELECTED LABEL (Vehicle / Vendor / Bunk)
  ================================ */
  const selectedLabel = (() => {
    if (vehicle) {
      return { title: "Vehicle", value: vehicle.vehicle_number };
    }

    if (vendor) {
      return {
        title: "Vendor",
        value:
          vendor.vendor_name +
          (vendor.vendor_vehicle_number
            ? ` (${vendor.vendor_vehicle_number})`
            : ""),
      };
    }

    if (bunk) {
      return { title: "Bunk", value: bunk.bunk_name };
    }

    return null;
  })();

  /* ===============================
     PDF GENERATOR
  ================================ */
  const generatePDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const headerEndY = drawClientHeader(doc, client);
    let currentY = headerEndY;

    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text(`Fuel Indent Report${fy ? ` (FY ${fy})` : ""}`, 14, currentY);
    currentY += 6;

    if (selectedLabel) {
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text(`${selectedLabel.title}: ${selectedLabel.value}`, 14, currentY);
      currentY += 6;
    }

    const columns = FUEL_INDENT_COLUMNS.map((c) => c.label);
    const body = rows.map((r) =>
      FUEL_INDENT_COLUMNS.map((c) => formatCell(c.key, r)),
    );

    autoTable(doc, {
      head: [columns],
      body,
      startY: currentY,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
    });

    // FOOTER (ALWAYS LAST)
    addPdfFooter(doc);

    return doc;
  };

  /* ===============================
     PRINT / PDF / EXCEL
  ================================ */
  const handlePrint = () => printPdfDoc(generatePDF());

  const handlePDF = () => {
    const doc = generatePDF();
    const url = URL.createObjectURL(doc.output("blob"));
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const handleExcel = () => {
    const data = rows.map((r) => {
      const obj = {};
      FUEL_INDENT_COLUMNS.forEach((c) => {
        obj[c.label] =
          c.key === "indent_date" ? formatDate(r[c.key]) : r[c.key];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fuel Indents");
    XLSX.writeFile(wb, `Fuel_Indent_Report${fy ? `_${fy}` : ""}.xlsx`);
  };

  /* ===============================
     UI
  ================================ */
  const datePickerProps = {
    inputStyle: {
      fontSize: 12,
      minHeight: 40,
      height: 40,
      padding: "2px 6px",
    },
    labelStyle: {
      fontSize: 12,
      lineHeight: 1,
      marginBottom: 0,
      top: -13,
      left: 8,
    },
    containerStyle: {
      width: 140,
      minHeight: 40,
      height: 40,
      marginBottom: 0,
    },
  };

  return (
    <Paper sx={{ p: 3 }}>
      {/* HEADER WITH BACK BUTTON */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate(REPORTS_PATH)}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              Fuel Indents
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            Fuel Indent Report
            {fy && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                FY {fy}
              </Typography>
            )}
          </Typography>
        </Box>
      </Box>

      {/* FILTERS */}
      <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">
        <MyDatePicker
          label="From Date"
          value={fromDate}
          onChange={setFromDate}
          {...datePickerProps}
        />

        <MyDatePicker
          label="To Date"
          value={toDate}
          onChange={setToDate}
          {...datePickerProps}
        />

        <TextField
          size="small"
          label="Search Indent / Vehicle / Driver / Bunk"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 180 }}
        />

        <Box sx={{ width: 180 }}>
          <VehicleDropdown
            clientId={clientId}
            vehicleId={vehicle?.vehicle_id || ""}
            onVehicleChange={(v) => {
              const selected = v && v.vehicle_id ? v : null;
              setVehicle(selected);
              if (selected) {
                setVendor(null);
                setFilterBunk("");
                setBunk(null);
              }
            }}
          />
        </Box>

        <Box sx={{ width: 220 }}>
          <VendorDropdown
            clientId={clientId}
            vendorId={vendor?.vendor_id || ""}
            onVendorChange={(v) => {
              const selected = v?.vendor_id ? v : null;
              setVendor(selected);
              if (selected) {
                setVehicle(null);
                setFilterBunk("");
                setBunk(null);
              }
            }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Bunk</InputLabel>
          <Select
            value={filterBunk}
            label="Bunk"
            onChange={(e) => {
              const selectedId = e.target.value;

              setFilterBunk(selectedId);

              const selectedBunk = bunks.find(
                (b) => Number(b.bunkid) === Number(selectedId),
              );
              setBunk(selectedBunk || null);

              // only one label at a time
              if (selectedId) {
                setVehicle(null);
                setVendor(null);
              }
            }}
          >
            <MenuItem value="">All</MenuItem>
            {bunks.map((b) => (
              <MenuItem key={b.bunkid} value={b.bunkid}>
                {b.bunk_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          size="small"
          sx={{ height: 40, minWidth: 60 }}
          onClick={() => loadReport()}
        >
          Search
        </Button>

        <Button
          variant="outlined"
          size="small"
          sx={{ height: 40, minWidth: 50 }}
          onClick={handlePrint}
        >
          Print
        </Button>

        <Button
          variant="outlined"
          size="small"
          color="error"
          sx={{ height: 40, minWidth: 50 }}
          onClick={handlePDF}
        >
          PDF
        </Button>

        <Button
          variant="outlined"
          size="small"
          color="success"
          sx={{ height: 40, minWidth: 45 }}
          onClick={handleExcel}
        >
          Excel
        </Button>
      </Box>

      {selectedLabel && (
        <Box
          sx={{
            mb: 1.5,
            px: 2,
            py: 1,
            backgroundColor: "#eef2f7",
            borderRadius: 1.5,
            fontSize: 16,
            fontWeight: 600,
            color: "#0f172a",
          }}
        >
          <span style={{ fontWeight: 700 }}>{selectedLabel.title}:</span>{" "}
          <span style={{ fontWeight: 600 }}>{selectedLabel.value}</span>
        </Box>
      )}

      {/* TABLE */}
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            width: "1200px",
            borderCollapse: "collapse",
            "& .MuiTableCell-root": {
              paddingLeft: "8px !important",
              paddingRight: "6px !important",
            },
          }}
        >
          <TableHead sx={{ background: "#f1f5f9" }}>
            <TableRow>
              {FUEL_INDENT_COLUMNS.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align || "left"}
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    width: c.width,
                    maxWidth: c.width,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={r.id ?? idx}>
                {FUEL_INDENT_COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align || "left"}
                    sx={{
                      fontSize: 11,
                      padding: "4px 6px",
                      width: c.width,
                      maxWidth: c.width,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {formatCell(c.key, r)}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={FUEL_INDENT_COLUMNS.length} align="center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
